import type { Log } from "../../log/Log";
import type { GridCells } from "../../view/grid/GridLayer";
import type { PresentationTaskRunner } from "../../runner/PresentationTaskRunner";

/**
 * Pure visual layer: highlights scatter positions (scale pulse). Driven only by the server action
 * `present_scatter_state`, which the engine AWAITS — the pulse paces the round.
 *
 * ALWAYS plays at NORMAL speed — a deliberate exception to clock-scaling: the durations are
 * multiplied by the clock's current time-scale, which cancels the runner's division by it.
 *
 * Lookup contract: cells are resolved by index via `getSymbolViewAtIndex`, so the grid must have
 * those indices BOUND when this runs. They are: when the last reel settles the grid adopts every landed
 * view, a landing still playing included, so nothing snaps the landing first (decision 20).
 */
export class ScatterHighlightEffect {
  private readonly gridView: GridCells;
  private readonly taskRunner: PresentationTaskRunner;
  private readonly log: Log;

  public constructor(input: { gridView: GridCells; taskRunner: PresentationTaskRunner; log: Log }) {
    this.gridView = input.gridView;
    this.taskRunner = input.taskRunner;
    this.log = input.log;
  }

  public async highlightScatterPositions(input: {
    positionIndices: number[];
    pulseScale?: number;
    pulseUpSeconds?: number;
    pulseDownSeconds?: number;
    /** Number of up→down cycles to run. Defaults to 1 (single pulse). */
    pulseCycles?: number;
    preDelayMilliseconds?: number;
    postDelayMilliseconds?: number;
  }): Promise<void> {
    const indices = input.positionIndices;
    if (indices.length === 0) return;

    const pulseScale = input.pulseScale ?? 1.18;
    const up = input.pulseUpSeconds ?? 0.35;
    const down = input.pulseDownSeconds ?? 0.45;
    const cycles = Math.max(1, Math.floor(input.pulseCycles ?? 1));
    const preDelay = (input.preDelayMilliseconds ?? 0) / 1000;
    const postDelay = (input.postDelayMilliseconds ?? 0) / 1000;

    // Normal-speed compensation: the runner divides durations by the clock scale.
    const scale = this.taskRunner.getTimeScale();
    const runner = this.taskRunner;

    if (preDelay > 0) await runner.sleep(preDelay * scale);

    const tasks: Promise<void>[] = [];

    for (const index of indices) {
      const view = this.gridView.getSymbolViewAtIndex(index);
      if (!view || view.node.destroyed) continue;

      const node = view.node;
      const base = { x: node.scale.x, y: node.scale.y };
      const high = { x: base.x * pulseScale, y: base.y * pulseScale };

      tasks.push(
        (async () => {
          for (let cycle = 0; cycle < cycles; cycle++) {
            await runner.tweenProgress(up * scale, (t01) => {
              if (node.destroyed) return;
              node.scale.set(base.x + (high.x - base.x) * t01, base.y + (high.y - base.y) * t01);
            });
            await runner.tweenProgress(down * scale, (t01) => {
              if (node.destroyed) return;
              node.scale.set(high.x + (base.x - high.x) * t01, high.y + (base.y - high.y) * t01);
            });
          }
          if (!node.destroyed) node.scale.set(base.x, base.y);
        })(),
      );
    }

    await Promise.all(tasks);

    if (postDelay > 0) await runner.sleep(postDelay * scale);

    this.log(`[ScatterHighlight] highlighted=${indices.length} cycles=${cycles}`);
  }
}
